import React, {useState} from 'react';
import AdminKedNavbar from "../../../components/admin/ked/AdminKedNavbar";
import AdminKedAdsResults from '../../../components/admin/ked/AdminKedAdsResults';
import {AdCampaign, DateRange, MetaService} from '../../../service/MetaService';
import Meta from "../../../components/common/Meta";
import MainDesign from "../../MainDesign";
import Heading from "../../../components/common/header/Heading";
import LoadingPage from "../../common/LoadingPage";
import ErrorContent from "../../../components/common/error/ErrorContent";

// Get default date range (2 weeks)
// JavaScript's Date automatically handles month/year boundaries correctly
// (e.g., if today is April 3, two weeks ago would be March 20)
const today = new Date();
const twoWeeksAgo = new Date();
twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14); // This works correctly even across month boundaries

const formatDateForApi = (date: Date) => {
    return date.toISOString().split('T')[0];
};

export default function AdminKedAdsResultsPage() {
    const [dateRange, setDateRange] = useState<DateRange>({
        since: formatDateForApi(twoWeeksAgo),
        until: formatDateForApi(today)
    });
    const [isLoading, setIsLoading] = useState(false);
    const [campaignsData, setCampaignsData] = useState<AdCampaign[] | null>(null);
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

    // Format date for display
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL');
    };

    // Handle date changes
    const handleSinceDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDateRange({...dateRange, since: e.target.value});
    };

    const handleUntilDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDateRange({...dateRange, until: e.target.value});
    };

    // Fetch campaigns with the selected date range
    const fetchCampaigns = async () => {
        setIsLoading(true);
        try {
            const newCampaigns = await MetaService.getAdsCampaigns(dateRange);
            setCampaignsData(newCampaigns);
            setHasInitiallyLoaded(true);
        } catch (error) {
            console.error("Error fetching campaigns:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Meta title="Wyniki reklam - KED Admin"/>
            <MainDesign containerStyles='container pt-20'>
                <Heading title="Wyniki reklam" badgeText='facebook ads'/>
                <AdminKedNavbar/>
                <hr className='text-white'/>

                {/* Date range selector */}
                <div className="card bg-white mb-4 p-3">
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div>
                                <label htmlFor="since-date" className="form-label mb-1">Od daty:</label>
                                <input
                                    type="date"
                                    id="since-date"
                                    className="form-control"
                                    value={dateRange.since}
                                    onChange={handleSinceDateChange}
                                />
                            </div>
                            <div>
                                <label htmlFor="until-date" className="form-label mb-1">Do daty:</label>
                                <input
                                    type="date"
                                    id="until-date"
                                    className="form-control"
                                    value={dateRange.until}
                                    onChange={handleUntilDateChange}
                                />
                            </div>
                        </div>
                        <div>
                            <button
                                className="btn btn-primary"
                                onClick={fetchCampaigns}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"
                                              aria-hidden="true"></span>
                                        Ładowanie...
                                    </>
                                ) : 'Zastosuj filtr'}
                            </button>
                        </div>
                    </div>
                    <div className="mt-2 text-muted small">
                        Dane wyświetlane dla okresu: {formatDate(dateRange.since)} - {formatDate(dateRange.until)}
                    </div>
                </div>

                {isLoading ? (
                    <LoadingPage styles="pt-10"/>
                ) : (
                    <>
                        {!hasInitiallyLoaded ? (
                            <div className="text-center py-5">
                                <div className="alert alert-info" role="alert">
                                    Wybierz zakres dat i kliknij "Zastosuj filtr", aby wyświetlić wyniki reklam.
                                </div>
                            </div>
                        ) : campaignsData ? (
                            <AdminKedAdsResults campaigns={campaignsData}/>
                        ) : (
                            <ErrorContent/>
                        )}
                    </>
                )}
            </MainDesign>
        </>
    );
}
